import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  input,
  linkedSignal,
  output
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { AddressInfo } from '@moxi.gmbh/moxi-typescriptmodels';
import { TranslatePipe } from '@ngx-translate/core';
import {
  areAllPropertiesNull,
  formatAddressDisplay,
  removeHtmlTags
} from '@shared/utils';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { PlaceAutocompleteComponent } from '@shared/components';

@Component({
  selector: 'moxi-address-form-group',
  imports: [
    FormsModule,
    ReactiveFormsModule,

    PlaceAutocompleteComponent,
    TranslatePipe,

    // Ant Design
    NzFormModule,
    NzInputModule,
    NzCheckboxModule
  ],
  templateUrl: './address-form-group.component.html',
  styleUrl: './address-form-group.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddressFormGroupComponent implements OnInit {
  formGroup = input.required<FormGroup>();
  addressInfo = input.required<AddressInfo>();
  showTitle = input(true);
  showAutocompleteLabel = input(true);
  required = input(true);

  addressChanged = output<AddressInfo>();

  address = linkedSignal<AddressInfo, AddressInfo>({
    source: this.addressInfo,
    computation: address => this.updateValues(address)
  });

  additionalInfo = linkedSignal(
    () => !!(this.address()?.instructions || this.address()?.addition)
  );

  searchTerm = computed<string | undefined>(() => this.setSearchTerm());

  autocomplete!: FormGroup;

  ngOnInit(): void {
    this.createForm();
    this.address.set(this.addressInfo());
  }

  onAutocompleteUpdate(address: AddressInfo): void {
    this.address.set(this.updateValues(address));
    this.addressChanged.emit(address);
  }

  private createForm(): void {
    this.formGroup()?.setControl(
      'city',
      new FormControl(this.addressInfo()?.city)
    );
    this.formGroup()?.setControl(
      'country',
      new FormControl(this.addressInfo()?.country)
    );
    this.formGroup()?.setControl(
      'state',
      new FormControl(this.addressInfo()?.state)
    );
    this.formGroup()?.setControl(
      'street',
      new FormControl(this.addressInfo()?.street)
    );
    this.formGroup()?.setControl(
      'addition',
      new FormControl(this.addressInfo()?.addition)
    );
    this.formGroup()?.setControl(
      'number',
      new FormControl(this.addressInfo()?.number)
    );
    this.formGroup()?.setControl(
      'zipCode',
      new FormControl(this.addressInfo()?.zipCode)
    );
    this.formGroup()?.setControl(
      'instructions',
      new FormControl(this.addressInfo()?.instructions)
    );
    this.formGroup()?.setControl(
      'geoLocation',
      new FormGroup({
        latitude: new FormControl(this.addressInfo()?.geoLocation?.latitude),
        longitude: new FormControl(this.addressInfo()?.geoLocation?.longitude)
      })
    );
    this.formGroup()?.setControl(
      'districtNutsCode',
      new FormControl(this.addressInfo()?.districtNutsCode)
    );

    this.formGroup()?.setControl('placeName', new FormControl());

    if (this.required()) {
      this.formGroup()?.get('city')?.setValidators(Validators.required);
      this.formGroup()?.get('street')?.setValidators(Validators.required);
      this.formGroup()?.get('number')?.setValidators(Validators.required);
      this.formGroup()?.get('zipCode')?.setValidators(Validators.required);
    }
  }

  private updateValues(address: AddressInfo): AddressInfo {
    if (areAllPropertiesNull(address)) {
      this.formGroup()?.reset(undefined, { emitEvent: false });
      return;
    }

    this.formGroup()?.controls?.autocomplete?.setErrors(undefined);
    this.formGroup()?.controls?.placeName?.reset();
    this.formGroup()?.patchValue(address);

    // remove autocomplete property from address info
    if (address['autocomplete']) {
      const { autocomplete, ...newAddress } = address as AddressInfo & {
        autocomplete: unknown;
      };
      address = newAddress;
    }

    return address;
  }

  private setSearchTerm(): string {
    return areAllPropertiesNull(this.address())
      ? undefined
      : removeHtmlTags(formatAddressDisplay(this.address()));
  }
}
